import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { Context } from "Context";
import Navigation from "./navigation/Navigation";
import useFetch from "hooks/useFetch";
import appConfig from "config.json";
import { Loading } from "@wdpr/groot-components";
import { mapString, isTravelAgent } from "utils";
import { getCurrentDateInSelectedLocale } from "utils/date";
import { filterContentByUserType } from "utils/filterContent";

// lazy loaded components
const Banner = lazy(() => import("./banner/Banner"));
const MessageBanner = lazy(() => import("./MessageBanner"));
const SmartBanner = lazy(() => import("./SmartBanner"));
const TravelAgentHeader = lazy(() =>
	import("../travel-agent-header/TravelAgentHeader")
);
const MessageCenter = lazy(() =>
	import("../message-center/MessageCenterWrapper")
);

const { brands, defaultParkHoursTimeZone, content } = appConfig;
const cacheMinutes = 5;
const mainClass = "syndicated-header";

const Header = () => {
	const {
		config,
		headerData,
		setHeaderData,
		setIsHeaderLoading,
		setConfig,
		profileData: { userType, user },
		isMobileView,
		cdnUrl,
	} = useContext(Context);

	const [headerUrl, setHeaderUrl] = useState("");
	const brandConfig = brands?.[config.brand];

	const parkHoursTimeZone =
		brandConfig?.defaultParkHoursTimeZone || defaultParkHoursTimeZone;
	const isUserTypeRequired = brandConfig?.isUserTypeRequiredForContentRequest;
	const { response, error } = useFetch(headerUrl, {}, cacheMinutes, headerUrl);

	let classes = `${mainClass} ${mainClass}--${config.brand} ${mainClass}--${config.locale}`;
	if (config.position) {
		classes += ` ${mainClass}--${config.position}`;
		if (config.position === "sticky") {
			const parentDiv = document?.querySelector(".syndicated-header-parent");
			parentDiv.classList.add("syndicated-header-parent--sticky");
		}
	}

	if (config.store) {
		classes += ` ${mainClass}__store ${mainClass}__store--${config.store}`;
	}

	if (config.theme) {
		classes += ` syndicated-theme--${config.theme}`;
	}

	useEffect(() => {
		if (!isUserTypeRequired || (isUserTypeRequired && userType)) {
			const headerBaseUrl = mapString(
				content.apiUrl.header,
				getCurrentDateInSelectedLocale(parkHoursTimeZone, "ISO", "en-us")
			);
			setHeaderUrl(
				isUserTypeRequired && userType
					? `${headerBaseUrl}&userType=${userType}&isMobileView=${isMobileView}`
					: `${headerBaseUrl}&isMobileView=${isMobileView}`
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userType, config.brand]);

	useEffect(() => {
		if (response) {
			/*
			 *if response is received, set loading to false no matther what *response is comming
			 */
			setIsHeaderLoading(false);
			if (brandConfig.filterContentKeys) {
				setHeaderData({
					...filterContentByUserType(
						response,
						brandConfig.filterContentKeys,
						user
					),
					loading: false,
				});
			} else {
				setHeaderData({ ...response, loading: false });
			}
			setConfig((config) => ({
				...config,
				...(response.context || {}),
			}));
		} else if (error) {
			setHeaderData({ error: true, loading: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error, response]);

	useEffect(() => {
		if (brandConfig?.filterContentKeys && response) {
			setHeaderData(
				filterContentByUserType(response, brandConfig.filterContentKeys, user)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.swid, user?.membership]);

	return (
		<div className={classes} style={{ visibility: "hidden" }}>
			{config.smartBanner && (
				<Suspense fallback={null}>
					<SmartBanner />
				</Suspense>
			)}
			{config.banner && (
				<Suspense fallback={null}>
					<Banner />
				</Suspense>
			)}
			{config.travelAgentHeader &&
				(config.store || (isUserTypeRequired && isTravelAgent(userType))) && (
					<Suspense fallback={null}>
						<TravelAgentHeader />
					</Suspense>
				)}
			{config.messageCenter && !document?.getElementById("messageCenter") && (
				<Suspense fallback={null}>
					<MessageCenter />
				</Suspense>
			)}
			{headerData.loading && <Loading brand={config.brand} cdnUrl={cdnUrl} />}
			{!config.hideFullNavigation &&
				!headerData.loading &&
				!headerData.error && (
					<Navigation showLogo={!config.navigation && !config.chrome} />
				)}
			{config.messageBanner && (
				<Suspense fallback={null}>
					<MessageBanner />
				</Suspense>
			)}
			<div id="pageContentStart" tabIndex={-1} />
		</div>
	);
};

export default Header;
